/*
  East Side Studio: live-drawn product cards.

  Cards with data-ess-drawn-card (see snippets/product-card.liquid) are drawn in the browser with the same renderer as the
  product page preview (window.ESSFrameRender, snippets/ess-drawn-overlay.liquid): the artwork in a frame of the chosen
  colour, at the card variant's shape. Two kinds, each with its own colour choice:
  - canvas: the floating frame (canvas collections), natural oak to start with
  - std: the standard frame without a mount (every other card), black to start with
  The frame switch ([data-ess-frame] buttons with data-ess-frame-kind, snippets/ess-frame-switch.liquid) redraws that
  kind's cards (kind "all": every card) and points their links at that colour's variant. The card photo stays underneath as the fallback, shown
  until the drawing is ready or if it fails.
  Config: window.ESS_DRAWN_CARDS = { tex: { canvas_black, canvas_white, canvas_natural, natural } } (texture images).
*/
(function () {
  'use strict';
  if (window.__essDrawnCards) return;
  window.__essDrawnCards = true;

  var CFG = window.ESS_DRAWN_CARDS || {};
  var COLOURS = ['black', 'white', 'natural'];
  var KINDS = {
    canvas: { store: 'ess-canvas-frame-colour', colour: 'natural', depth: 'canvas' },
    std: { store: 'ess-frame-colour', colour: 'black', depth: 'std' }
  };
  var W = 900, H = 1200;   /* drawing size: the cards' 3:4 shape */
  var FILL = 0.84;         /* share of the card the frame fills (the product page scales by print size instead) */
  var DRAW_SHORT_MM = 610; /* cards draw a mid size of the variant's shape (about 24" on the short side), so the frame
                              reads as it does on a typical print rather than as thick as on the smallest size */

  Object.keys(KINDS).forEach(function (kind) {
    try { var saved = localStorage.getItem(KINDS[kind].store); if (COLOURS.indexOf(saved) !== -1) KINDS[kind].colour = saved; } catch (e) {}
  });

  function loadImage(src) {
    return new Promise(function (resolve, reject) {
      var image = new Image();
      image.crossOrigin = 'anonymous';
      image.decoding = 'async';
      image.onload = function () { resolve(image); };
      image.onerror = reject;
      image.src = src;
    });
  }

  /* the textures a frame draws with: the canvas face strips, the natural oak classic frame (black / white are solid) */
  var textures = {}, texturePromises = {};
  function textureFor(kind, frame) {
    var key = kind === 'canvas' ? 'canvas_' + frame : frame === 'natural' ? 'natural' : null;
    if (!key) return Promise.resolve();
    if (!texturePromises[key]) {
      texturePromises[key] = loadImage(CFG.tex && CFG.tex[key]).then(function (image) { textures[key] = image; });
    }
    return texturePromises[key];
  }

  var artPromises = new Map();
  function artFor(src) {
    if (!artPromises.has(src)) artPromises.set(src, loadImage(src));
    return artPromises.get(src);
  }

  function drawSize(sizeKey) {
    var R = window.ESSFrameRender;
    var size = R.SIZES.filter(function (s) { return s.sizeKey === sizeKey; })[0];
    if (!size) return null;
    var ratio = size.heightMm / size.widthMm, best = size;
    R.SIZES.forEach(function (s) {
      if (Math.abs(s.heightMm / s.widthMm - ratio) > 0.01) return;
      if (Math.abs(s.widthMm - DRAW_SHORT_MM) < Math.abs(best.widthMm - DRAW_SHORT_MM)) best = s;
    });
    return best;
  }

  /* one drawn frame per kind / size / orientation / colour, scaled so it fills the card */
  var frames = new Map();
  function frameFor(kind, sizeKey, ori, frame) {
    var R = window.ESSFrameRender;
    var key = kind + '|' + sizeKey + '|' + ori + '|' + frame;
    if (frames.has(key)) return frames.get(key);
    var size = drawSize(sizeKey);
    if (!size) return null;
    var base = R.SET.mockupScaleMultiplier, result = null;
    try {
      var D = R.dims(size, ori, true, false, W, H);
      R.SET.mockupScaleMultiplier = base * Math.min(FILL * H / D.frameH, FILL * W / D.frameW);
      result = R.overlay({ size: size.sizeKey, ori: ori, frame: frame, depth: KINDS[kind].depth, mount: 'm0' }, textures, W, H);
    } finally {
      R.SET.mockupScaleMultiplier = base;
    }
    frames.set(key, result);
    return result;
  }

  function cardData(card) {
    if (card.__essData === undefined) {
      try { card.__essData = JSON.parse(card.getAttribute('data-ess-drawn-card')); } catch (e) { card.__essData = null; }
      if (card.__essData && !KINDS[card.__essData.kind]) card.__essData = null;
    }
    return card.__essData;
  }

  function colourFor(data) { return KINDS[data.kind].colour; }

  function variantFor(data, frame) {
    return data.v[frame] || null;
  }

  /* links, quick buy and (canvas) the fallback photo follow the chosen colour */
  function pointAt(card, variant) {
    if (!variant) return;
    card.querySelectorAll('.product-card__figure > a, .product-card__title a').forEach(function (link) { link.setAttribute('href', variant.url); });
    var drawer = card.querySelector('quick-buy-drawer[handle]');
    if (drawer) drawer.setAttribute('handle', drawer.getAttribute('handle').split('?')[0] + '?variant=' + variant.id);
    var photo = card.querySelector('.product-card__image--primary');
    if (photo && variant.img && photo.getAttribute('data-ess-img') !== variant.img) {
      photo.removeAttribute('srcset'); photo.removeAttribute('sizes');
      photo.setAttribute('src', variant.img); photo.setAttribute('data-ess-img', variant.img);
    }
  }

  function screenFor(card) {
    var link = card.querySelector('.product-card__figure > a');
    if (!link) return null;
    var screen = link.querySelector(':scope > canvas.ess-drawn-card');
    if (!screen) {
      screen = document.createElement('canvas');
      screen.className = 'ess-drawn-card';
      screen.width = W; screen.height = H;
      screen.setAttribute('aria-hidden', 'true');
      link.classList.add('ess-drawn-card-link');
      link.appendChild(screen);
    }
    return screen;
  }

  /* photos stay hidden (html.ess-drawn-wait, layout/theme.liquid) until the card is drawn; this brings one back */
  function showPhoto(card) {
    card.classList.remove('ess-drawn-card--drawn');
    card.classList.add('ess-drawn-card--photo');
  }

  function draw(card) {
    var data = cardData(card);
    if (!data) return showPhoto(card);
    var frame = colourFor(data), variant = variantFor(data, frame);
    if (!variant) {   /* no variant in this colour: show the photo */
      showPhoto(card);
      card.__essDrawn = frame;
      return;
    }
    pointAt(card, variant);
    card.__essWant = frame;
    Promise.all([textureFor(data.kind, frame), artFor(data.art)]).then(function (loaded) {
      if (card.__essWant !== frame) return;
      var art = loaded[1], drawn = frameFor(data.kind, data.size, data.ori, frame), screen = screenFor(card);
      if (!drawn || !screen) return showPhoto(card);
      var g = screen.getContext('2d'), win = drawn.window;
      g.clearRect(0, 0, W, H);
      /* the artwork fills the frame opening (cover), as on the product page */
      var s = Math.max(win.w / art.naturalWidth, win.h / art.naturalHeight), cw = win.w / s, ch = win.h / s;
      g.imageSmoothingQuality = 'high';
      g.drawImage(art, (art.naturalWidth - cw) / 2, (art.naturalHeight - ch) / 2, cw, ch, win.x, win.y, win.w, win.h);
      g.drawImage(drawn.canvas, 0, 0);
      card.classList.add('ess-drawn-card--drawn');
      card.classList.remove('ess-drawn-card--photo');
      card.__essDrawn = frame;
    }).catch(function () { if (card.__essWant === frame) showPhoto(card); });
  }

  /* draw cards as they come near the screen */
  var seen = new WeakSet();
  var observer = 'IntersectionObserver' in window ? new IntersectionObserver(function (entries) {
    entries.forEach(function (entry) {
      if (!entry.isIntersecting) return;
      var data = cardData(entry.target);
      entry.target.__essVisible = true;
      if (data && entry.target.__essDrawn !== colourFor(data)) draw(entry.target);
    });
  }, { rootMargin: '400px 0px' }) : null;

  function cards(kind) {
    return Array.prototype.filter.call(document.querySelectorAll('product-card[data-ess-drawn-card]'), function (card) {
      var data = cardData(card);
      return data && (!kind || data.kind === kind);
    });
  }

  function track() {
    cards().forEach(function (card) {
      if (seen.has(card)) return;
      seen.add(card);
      var data = cardData(card);
      pointAt(card, variantFor(data, colourFor(data)));
      if (observer) observer.observe(card); else draw(card);
    });
  }

  function syncSwitches() {
    document.querySelectorAll('[data-ess-frame]').forEach(function (button) {
      var name = button.getAttribute('data-ess-frame-kind'), kind = KINDS[name === 'all' ? 'std' : name];
      if (kind) button.setAttribute('aria-pressed', button.getAttribute('data-ess-frame') === kind.colour ? 'true' : 'false');
    });
  }

  document.addEventListener('click', function (event) {
    var button = event.target.closest && event.target.closest('[data-ess-frame]');
    if (!button) return;
    var kindName = button.getAttribute('data-ess-frame-kind'), next = button.getAttribute('data-ess-frame');
    var names = kindName === 'all' ? Object.keys(KINDS) : KINDS[kindName] ? [kindName] : [];
    if (!names.length || COLOURS.indexOf(next) === -1) return;
    names = names.filter(function (name) { return KINDS[name].colour !== next; });
    if (!names.length) return;
    names.forEach(function (name) {
      KINDS[name].colour = next;
      try { localStorage.setItem(KINDS[name].store, next); } catch (e) {}
    });
    syncSwitches();
    cards().filter(function (card) { return names.indexOf(cardData(card).kind) !== -1; }).forEach(function (card) {
      if (!observer || card.__essVisible) draw(card);
      else pointAt(card, variantFor(cardData(card), next));
    });
  });

  function start() {
    if (!window.ESSFrameRender) { document.documentElement.classList.remove('ess-drawn-wait'); return; }
    /* one switch for the whole page (homepage): canvas cards follow the colour it shows */
    if (document.querySelector('[data-ess-frame-kind="all"]')) KINDS.canvas.colour = KINDS.std.colour;
    syncSwitches();
    track();
    /* cards added later (filters, sorting, load more, recommendations): checked at most once per frame */
    var pending = false;
    new MutationObserver(function (mutations) {
      if (pending) return;
      for (var i = 0; i < mutations.length; i++) {
        if (mutations[i].addedNodes.length) {
          pending = true;
          requestAnimationFrame(function () { pending = false; track(); syncSwitches(); });
          return;
        }
      }
    }).observe(document.body, { childList: true, subtree: true });
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start); else start();
})();
